import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@material-ui/core';
import { useRouter } from 'next/router';
import { getBrochureRequest } from '../../store/slices/brochure';
import { SearchStatus, BrochureTemplateId, TravelTypes } from '../../constants';
import withSpinner from '../../HOCs/withSpinner';
import SegmentCard from './SegmentCard';
import EmailToClientDetails from '../EmailToClientForm/EmailToClientDetails';
import ModalSpinnerSaveTrip from '../ModalSpinner/ModalSpinnerSaveTrip';
import TipTapEditor from '../EmailToClientForm/TipTapEditor';
import { showSpinner, richTextButtonStyles } from '../EmailToClientForm';
import PaperTemplate from './PaperTemplate';

const renderingOrder = [
  TravelTypes.cruise,
  TravelTypes.flight,
  TravelTypes.hotel,
  TravelTypes.carrental,
  TravelTypes.tour,
  TravelTypes.other,
];

const GROUP_LABELS = {
  [TravelTypes.cruise]: 'Cruises',
  [TravelTypes.flight]: 'Flights',
  [TravelTypes.hotel]: 'Hotels',
  [TravelTypes.carrental]: 'Rental Cars',
  [TravelTypes.tour]: 'Tours',
  [TravelTypes.other]: 'Itinerary',
};

const groupSegmentsByType = (trips) => {
  const grouped = {};
  (trips || []).forEach((trip) => {
    (trip.tripSegments || []).forEach((segment) => {
      const mode = segment?.mode ?? TravelTypes.other;
      if (!grouped[mode]) grouped[mode] = [];
      grouped[mode].push({ segment, trip });
    });
  });
  return grouped;
};

const segmentKey = ({ segment, trip }) => {
  const it = segment?.itinerary || {};
  const innerKey =
    it.flightKey ||
    it.hotelKey ||
    it.uniqueKey ||
    it.itineraryDetail?.itineraryId ||
    it.id ||
    segment?.id ||
    '';
  return `${trip?.id || trip?.longTripTitle || 'trip'}-${segment?.mode ?? 'x'}-${innerKey}`;
};

const renderGroupedSegments = (trips, doShowPrice) => {
  const segmentsByType = groupSegmentsByType(trips);
  return renderingOrder.map((type) => {
    const items = segmentsByType[type];
    if (!items || items.length === 0) return null;
    return (
      <div key={type} className="mb-8">
        <p className="text-sm uppercase text-gray-500 font-semibold tracking-wide mb-2 px-2">
          {GROUP_LABELS[type]}
        </p>
        {items.map(({ segment, trip }) => (
          <SegmentCard
            key={segmentKey({ segment, trip })}
            segment={segment}
            trip={trip}
            doShowPrice={doShowPrice}
          />
        ))}
      </div>
    );
  });
};

const Brochure = ({ id }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { getBrochureStatus, getBrochureError } = useSelector((store) => store.brochure);
  const {
    sendBrochureRequestStatus,
    isSaveBrochureContent,
    doShowBrochureEditor: activeBrochureShowEditor,
  } = useSelector((state) => state.brochure.brochure);
  const brochure = useSelector((state) => state.brochure.brochures[id]);
  const { travelAgency, isLoggedIn } = useSelector((state) => state.user);
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;

  const brochureSettings =
    (travelAgency && travelAgency.settings && travelAgency.settings.brochureSettings) || {};
  const { pageHeading, pageSubHeading, DisplayName, doHideMessageBoxInPage, doShowBrochureEditor } =
    brochureSettings;
  const displayLabel = DisplayName?.toLowerCase() || 'Brochure';
  useEffect(() => {
    if (!brochure && getBrochureStatus === SearchStatus.INITIAL) {
      dispatch(getBrochureRequest({ id }));
    }
  }, [brochure, id, dispatch, getBrochureStatus]);

  if (!brochure) {
    if (getBrochureError && getBrochureStatus === SearchStatus.DONE) {
      return <div className="text-center py-20 text-xl">Error: {getBrochureError}</div>;
    }
  }

  if (brochure && router.query.templateId === BrochureTemplateId.PAPER) {
    return <PaperTemplate brochure={brochure} isLoggedIn={isLoggedIn} />;
  }

  const renderComponent = () => (
    <div>
      {brochure && (
        <div className="mx-auto flex justify-between w-85 grid grid-cols-3 gap-4">
          <EmailToClientDetails
            isSavedToDatabase={brochure.isSavedToDatabase}
            createdFor={brochure.createdFor}
            fromEmail={brochure.fromEmail}
            emailRecipients={brochure.emailRecipients}
            emailSubject={brochure.emailSubject}
            emailMessage={brochure.emailMessage}
            headerPrefix={`${displayLabel.charAt(0).toUpperCase() + displayLabel.slice(1)} for`}
            brochureId={brochure.id}
            pageHeading={pageHeading}
            pageSubHeading={pageSubHeading}
            doHideMessageBoxInPage={doHideMessageBoxInPage}
          />
          {brochure &&
            brochure.id &&
            !!Object.keys(brochure.trips).length &&
            brochure.editorJSON &&
            isBrochurePage &&
            doShowBrochureEditor &&
            activeBrochureShowEditor !== false && (
              <div className="inline-block w-full col-span-3 bg-white rounded sm:p-5 p-2 text-sm sm:text-base">
                <TipTapEditor
                  brochure={brochure}
                  isEditable={isLoggedIn}
                  isBrochurePage={isBrochurePage}
                  showSpinner={showSpinner}
                  richTextButtonStyles={richTextButtonStyles}
                />
              </div>
            )}
          <div className="inline-block w-full col-span-3">
            <p className="sm:text-2xl text-xl sm:pl-0 pl-2 font-bold">
              {brochure.trips.length > 1
                ? `Your ${displayLabel} options:`
                : `Your ${displayLabel} option:`}
            </p>
            {isBrochurePage ? (
              <>
                {sendBrochureRequestStatus === SearchStatus.INITIAL && (
                  <div className="w-full flex justify-center items-center">
                    <CircularProgress />
                    <div className="ml-2">
                      Please wait, {isSaveBrochureContent ? 'saving' : 'removing'}
                      ...
                    </div>
                  </div>
                )}
                {sendBrochureRequestStatus !== SearchStatus.INITIAL &&
                  brochure.trips.length > 0 && (
                    <div className="m-4 mr-0 ml-0">
                      {renderGroupedSegments(brochure.trips, brochure.doShowPricing)}
                    </div>
                  )}
                {sendBrochureRequestStatus !== SearchStatus.INITIAL &&
                  brochure.trips.length === 0 && (
                    <div className="w-full text-center py-8 text-gray-500">
                      No trips found in the brochure.
                    </div>
                  )}
              </>
            ) : (
              <div className="m-4 mr-0 ml-0">
                {renderGroupedSegments(brochure.trips, brochure.doShowPricing)}
              </div>
            )}
          </div>
        </div>
      )}
      {brochure && (brochure.doAllowBook || isLoggedIn) && isBrochurePage && (
        <ModalSpinnerSaveTrip tripId={brochure.id} />
      )}
    </div>
  );

  return withSpinner(
    renderComponent,
    getBrochureStatus === SearchStatus.SEARCHING,
    `Searching ${displayLabel}...`,
  );
};

Brochure.propTypes = {
  id: PropTypes.string.isRequired,
};

export default Brochure;
